import React, { useMemo } from 'react'
import { Icon, Text, Touchable, View } from '@/components'
import { FeedComment } from '../molecules/FeedComment'
import { CommunityComment } from '@/types'
import { variantProvider } from '@/app'
import { APIClient } from '@/services'
import { TypeGuards } from '@codeleap/types'

const FooterButton = ({ onPress, text, icon }) => {
  return (
    <Touchable onPress={onPress} debugName='Show more replies'>
      <View style={styles.line} />
      <Text
        text={text}
        variants={['p4', 'color:neutral7', 'bold', 'cursor:pointer']}
      />
      <Icon
        debugName=''
        name={icon}
        variants={['neutral5', 'iconSize:2', 'cursor:pointer']}
      />
    </Touchable>
  )
}

type CommentWithRepliesProps = {
  comment: CommunityComment
  onReport?: () => void
  onReply: (comment?: CommunityComment) => void
}

export const CommentWithReplies = ({ comment, onReport, onReply }: CommentWithRepliesProps) => {
  const isOptimistic = TypeGuards.isString(comment?.id)
  
  const {
    replies,
    showViewMore,
    showHideAll,
    commentsCount,
    hasViewedMore,
    handleViewMore,
    handleHideAll,
    query,
    hasReplies,
  } = APIClient.Community.useCommentReplies(comment)

  const footerComponent = useMemo(() => {
    if (showViewMore) {
      return (
        <FooterButton
          text={hasViewedMore ? `View more replies` : `Show more ${commentsCount} comments`}
          onPress={handleViewMore}
          icon='chevron-down'
        />
      )
    }

    if (showHideAll) {
      return (
        <FooterButton
          text='Hide replies'
          onPress={handleHideAll}
          icon='chevron-up'
        />
      )
    }

    return null
  }, [showHideAll, showViewMore, hasViewedMore, commentsCount, handleViewMore, handleHideAll])

  if (!comment) return null

  if (!hasReplies || query.isPending) {
    return <FeedComment comment={comment} onReport={onReport} onReply={isOptimistic ? null : () => onReply(comment)} />
  }

  return (
    <View variants={['fullWidth', 'column']}>
      <FeedComment comment={comment} onReport={onReport} onReply={isOptimistic ? null : () => onReply(comment)} />

      {hasReplies ? (
        <View variants={['column', 'gap:1', 'paddingLeft:4', 'paddingBottom: 1']}>
        {replies?.map(reply => (
          <FeedComment
            key={reply?.id}
            comment={reply}
            onReport={onReport}
            onReply={TypeGuards.isNumber(reply?.id) ? () => onReply(reply) : null}
          />
        ))}

        {footerComponent}
      </View>
      ) : null}
    </View>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    line: {
      width: 32,
      height: 1,
      backgroundColor: theme.colors.neutral3,
      ...theme.spacing.marginRight(1),
    },
  }),
  true
)
